/**
 * RuleDialog - create or edit a single `WorkflowStateCheck` attached to a
 * workflow state.
 *
 * A check runs at a state boundary chosen by `stage`:
 *  - ENTRY: evaluated as the engine enters this state. `outcome` MOVE
 *    redirects to `target_state`; NONE only raises an issue.
 *  - EXIT: evaluated as a user action leaves this state. `outcome` DIVERT
 *    blocks the action and runs `divert_to_action` instead (optionally
 *    scoped to `applies_to_actions`); NONE (BLOCK) blocks the exit.
 *
 * `severity` (BLOCK / WARN / INFO) governs whether downstream actions are
 * blocked and how the issue is rendered. The `conditions` predicate uses the
 * shared ConditionBuilder seeded with refs from the workflow's resource type.
 */

import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateStateCheck,
  useUpdateStateCheck,
  useWorkflowDetail,
  type WorkflowStateCheck,
  type WorkflowRuleOutcome,
  type WorkflowRuleSeverity,
  type WorkflowStateCheckStage,
  type WorkflowStateAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { ConditionField } from './action-modal/condition-field'
import { normalisePredicate } from './action-modal/predicate-utils'
import { useWorkflowConditionRefs } from './action-modal/use-resource-refs'

interface RuleDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  mode: 'create' | 'edit'
  stateUuid: string
  workflowUuid: string
  workflowKey: string
  resourceType?: string
  states: WorkflowStateAdmin[]
  rule?: WorkflowStateCheck
}

type RuleFormValues = {
  name: string
  description: string
  conditions: PredicateNode | null
  stage: WorkflowStateCheckStage
  severity: WorkflowRuleSeverity
  outcome: WorkflowRuleOutcome
  target_state: string | null
  divert_to_action: string | null
  applies_to_actions: string
  priority: number
  is_active: boolean
  message: string
  issue_title: string
  issue_description: string
}

const STAGE_OPTIONS: { value: WorkflowStateCheckStage; label: string }[] = [
  { value: 'ENTRY', label: 'Entry - when the instance arrives at this state' },
  { value: 'EXIT', label: 'Exit - when a user action leaves this state' },
]

const SEVERITY_OPTIONS: { value: WorkflowRuleSeverity; label: string }[] = [
  { value: 'BLOCK', label: 'Block - prevent / divert the transition' },
  { value: 'WARN', label: 'Warn - surface as warning issue' },
  { value: 'INFO', label: 'Info - surface informational issue' },
]

// Outcome options span both stages; the backend validates the stage/outcome
// combination (ENTRY: MOVE/NONE, EXIT: DIVERT/NONE) on save.
const OUTCOME_OPTIONS: { value: WorkflowRuleOutcome; label: string }[] = [
  { value: 'NONE', label: 'None - raise issue only' },
  { value: 'MOVE', label: 'Move - redirect to target state (entry)' },
  { value: 'DIVERT', label: 'Divert - run another action instead (exit)' },
]

export function RuleDialog({
  open,
  onOpenChange,
  mode,
  stateUuid,
  workflowUuid,
  workflowKey,
  resourceType,
  states,
  rule,
}: RuleDialogProps) {
  const create = useCreateStateCheck(stateUuid)
  const update = useUpdateStateCheck(stateUuid)
  const detail = useWorkflowDetail(workflowUuid)
  const {
    refs,
    byRef,
    isLoading: refsLoading,
  } = useWorkflowConditionRefs({
    workflowKey,
    resourceType,
  })

  // Actions runnable from this state - the pool of divert targets and the
  // actions an exit check can govern.
  const stateActions = useMemo(
    () =>
      (detail.data?.current_version?.actions ?? []).filter(
        (a) => a.applicability === 'STATE' && a.source_state === stateUuid,
      ),
    [detail.data, stateUuid],
  )

  const initial = useMemo<RuleFormValues>(
    () => ({
      name: rule?.name ?? '',
      description: rule?.description ?? '',
      conditions: normalisePredicate(rule?.conditions),
      stage: rule?.stage ?? 'ENTRY',
      severity: rule?.severity ?? 'BLOCK',
      outcome: rule?.outcome ?? 'NONE',
      target_state: rule?.target_state ?? null,
      divert_to_action: rule?.divert_to_action ?? null,
      applies_to_actions: (rule?.applies_to_actions ?? []).join(', '),
      priority: rule?.priority ?? 100,
      is_active: rule?.is_active ?? true,
      message: rule?.message ?? '',
      issue_title: rule?.issue_title ?? '',
      issue_description: rule?.issue_description ?? '',
    }),
    [rule],
  )

  const stateOptions = useMemo(
    () =>
      [...states]
        .sort((a, b) => a.order - b.order)
        .map((s) => ({
          value: s.uuid,
          label: `${s.label} (${s.key})`,
        })),
    [states],
  )

  const fields = useMemo<FormField[]>(() => {
    const list: FormField[] = [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        value: initial.name,
        required: 'Name is required',
        className: 'col-span-2',
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'What this check guards against',
        value: initial.description,
        className: 'col-span-2',
      },
      {
        type: 'custom',
        name: 'conditions',
        value: initial.conditions,
        className: 'col-span-2',
        render: (formCtx) => (
          <ConditionField
            label="Conditions"
            hint="Check fires when this predicate evaluates to true against the resource + workflow attributes."
            value={normalisePredicate(
              formCtx.values.conditions as PredicateNode | null | undefined,
            )}
            onChange={(next) => formCtx.setFieldValue('conditions', next)}
            refsLoading={refsLoading}
            refs={refs}
            byRef={byRef}
          />
        ),
      },
      {
        type: 'select',
        name: 'stage',
        label: 'Stage',
        value: initial.stage,
        options: STAGE_OPTIONS,
        className: 'col-span-1',
      },
      {
        type: 'select',
        name: 'severity',
        label: 'Severity',
        value: initial.severity,
        options: SEVERITY_OPTIONS,
        className: 'col-span-1',
      },
      {
        type: 'select',
        name: 'outcome',
        label: 'Outcome',
        value: initial.outcome,
        options: OUTCOME_OPTIONS,
        className: 'col-span-1',
      },
      {
        type: 'number',
        name: 'priority',
        label: 'Priority',
        value: initial.priority,
        className: 'col-span-1',
      },
      {
        type: 'custom',
        name: 'target_state',
        value: initial.target_state,
        className: 'col-span-1',
        render: (formCtx) => {
          const outcome = formCtx.values.outcome as WorkflowRuleOutcome
          if (outcome !== 'MOVE') return <div />
          const value = (formCtx.values.target_state as string | null) ?? ''
          return (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Target state
              </Label>
              <Select
                value={value}
                onValueChange={(next: string) =>
                  formCtx.setFieldValue('target_state', next || null)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="- Select state -" />
                </SelectTrigger>
                <SelectContent>
                  {stateOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )
        },
      },
      {
        type: 'custom',
        name: 'divert_to_action',
        value: initial.divert_to_action,
        className: 'col-span-1',
        render: (formCtx) => {
          const outcome = formCtx.values.outcome as WorkflowRuleOutcome
          if (outcome !== 'DIVERT') return <div />
          const value = (formCtx.values.divert_to_action as string | null) ?? ''
          return (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Divert to action
              </Label>
              <Select
                value={value}
                onValueChange={(next: string) =>
                  formCtx.setFieldValue('divert_to_action', next || null)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="- Select action -" />
                </SelectTrigger>
                <SelectContent>
                  {stateActions.map((a) => (
                    <SelectItem key={a.uuid} value={a.uuid}>
                      {a.label} ({a.key})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )
        },
      },
      {
        type: 'custom',
        name: 'applies_to_actions',
        value: initial.applies_to_actions,
        className: 'col-span-2',
        render: (formCtx) => {
          const outcome = formCtx.values.outcome as WorkflowRuleOutcome
          if (outcome !== 'DIVERT') return <div />
          const value = (formCtx.values.applies_to_actions as string) ?? ''
          const available = stateActions.map((a) => a.key).join(', ')
          return (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Applies to actions
              </Label>
              <Input
                value={value}
                placeholder="Comma-separated action keys; blank = all"
                onChange={(e) =>
                  formCtx.setFieldValue('applies_to_actions', e.target.value)
                }
              />
              <span className="text-[10px] text-[var(--ink-3)]">
                {available
                  ? `Available: ${available}`
                  : 'Actions leaving this state populate here once saved.'}
              </span>
            </div>
          )
        },
      },
      {
        type: 'text',
        name: 'message',
        label: 'Message',
        placeholder: 'Optional human-readable summary',
        value: initial.message,
        className: 'col-span-2',
      },
      {
        type: 'text',
        name: 'issue_title',
        label: 'Issue title',
        placeholder: 'Title shown on the raised WorkflowIssue',
        value: initial.issue_title,
        className: 'col-span-2',
      },
      {
        type: 'rich-text',
        name: 'issue_description',
        label: 'Issue description',
        placeholder: 'Body shown on the raised WorkflowIssue',
        value: initial.issue_description,
        className: 'col-span-2',
      },
      {
        type: 'checkbox',
        name: 'is_active',
        label: 'Active',
        value: initial.is_active,
        className: 'col-span-2',
      },
    ]
    return list
  }, [initial, refs, byRef, refsLoading, stateOptions, stateActions])

  const { Form, ref } = useForm(
    {
      name: `workflow-rule-${mode}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as RuleFormValues
      const isExit = values.stage === 'EXIT'
      const isDivert = values.outcome === 'DIVERT'
      const payload = {
        name: values.name.trim(),
        description: values.description ?? '',
        conditions: values.conditions ?? {},
        stage: values.stage,
        severity: values.severity,
        outcome: values.outcome,
        target_state:
          values.outcome === 'MOVE' ? values.target_state || null : null,
        divert_to_action: isDivert ? values.divert_to_action || null : null,
        applies_to_actions:
          isExit && isDivert
            ? (values.applies_to_actions ?? '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        priority: Number(values.priority) || 0,
        is_active: Boolean(values.is_active),
        message: values.message ?? '',
        issue_title: values.issue_title ?? '',
        issue_description: values.issue_description ?? '',
      }
      try {
        if (mode === 'edit' && rule) {
          await update.mutateAsync({ uuid: rule.uuid, ...payload })
          toast.success('Check updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Check created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, `Failed to ${mode} check`)
      }
    },
    [open, mode, rule?.uuid, fields],
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) ref.current?.resetForm()
      onOpenChange(next)
    },
    [onOpenChange, ref],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit Check' : 'New Check'}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={busy}
            data-testid="workflows-rule-submit"
          >
            {busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
